/**
 * The Octo Gadget (1.10, owner decision, 30 October 2026: "Add an octo-gadget, if found, it gives the user 4 octo hands,
 * similar to Spiderman, its a handheld melee tool, that sits on the back, its pretty much a 40m grapple hook, pulls the
 * player the direction its shot at (one of the arms, randomly, is pulled), this can be triggered by picking the
 * gadget/melee and press left (like shooting), it can also be quick used by precesing shift ... every arm, when shot,
 * deals 15 damage if it hits, the arm that hit pulls back slowly and another trigger sends another arm, if all four are
 * still going back the player has to wait, 2 second pullback for each octo arm, it has to be animated nice, like its
 * alive, the arms, like it has its own brain").
 *
 * A melee weapon (`octo_gadget` in definitions/items/melees.ts, found as loot) that is a harness on the back with four
 * mechanical arms, the way Doctor Octopus wears his. Whoever carries it has the arms, whatever they hold: attack with
 * the gadget in hand, or the octo key (Shift by default) with anything in hand, shoots one of the arms that are ready,
 * picked at random, straight out the way the player aims, `reach` units at most:
 *  - a player it meets on the way is struck for `damage` (armour counts, as with any melee), and the arm comes back,
 *    slowly (`returnMs`), the player not pulled;
 *  - a wall, a tree, a crate or anything else solid it meets, or the ground at the end of its reach, it clamps onto, and
 *    pulls its player there at `pullSpeed` (a grapple hook: the player's own steps don't count meanwhile, their aim and
 *    their gun do), letting go within `stopDistance` of its hold, or once the player is held up (a wall, another player)
 *    for `blockedMs`; then it comes back, as slowly.
 * An arm coming back can't be shot: with all four coming back, the player waits. Nothing is shot from a plane, under a
 * chute, on a ladder, downed or dead; a death ends everything at once, and so does dropping the gadget, but the arms
 * are then as they were when it's taken up again (1.10.1: dropped and picked up, it gave four new arms at once).
 *
 * The server decides it all (server/src/octo/octoSystem.ts) and tells everyone each arm's state and where its claw is
 * (`OctoArmData`, in the player's partial data: `octo`); the page draws the arms, alive, from the harness
 * (client/src/scripts/octo/octoFx.ts). Distances are game units (a player is 2.25 across the middle), speeds units a
 * millisecond (a run is 0.03), times milliseconds.
 */
export const OctoRig = Object.freeze({
    /** the gadget's item id (definitions/items/melees.ts) */
    idString: "octo_gadget",
    /** the arms on the harness */
    arms: 4,
    /** how far an arm shoots (the owner's "40m") */
    reach: 40,
    /** how fast it flies out (its reach in a fifth of a second) */
    flySpeed: 0.2,
    /** how fast it pulls its player to its hold (three and a third runs) */
    pullSpeed: 0.1,
    /** how long an arm takes to come back, wherever it was (the owner: "2 second pullback for each octo arm") */
    returnMs: 2000,
    /** what a struck player takes (before armour) */
    damage: 15,
    /** the claw's reach either side of its line: whoever and whatever this close to it is met */
    clawRadius: 1.3,
    /** the pull lets go this close to its hold (a wall's face, or the ground where the arm came down) */
    stopDistance: 2.4,
    /** ...or once the player is held up (a wall between, another player) this long */
    blockedMs: 160,
    /**
     * where the arms leave the harness, behind the player's middle and out to the sides (the player's own units, facing
     * +x): the two high arms (0 left, 1 right) from the harness's shoulders, the two low ones (2 left, 3 right) from its
     * base, nearer the spine
     */
    mounts: Object.freeze([
        Object.freeze({ back: 0.95, side: -1.35 }),
        Object.freeze({ back: 0.95, side: 1.35 }),
        Object.freeze({ back: 1.55, side: -0.7 }),
        Object.freeze({ back: 1.55, side: 0.7 })
    ]),
    /** the arm's claw clamps this far short of the wall it meets (its own length) */
    clawLength: 1.4,
    /** one shot at most this often (a tap, not a stream) */
    shotCooldownMs: 150,
    /**
     * the furthest from its player the wire says a claw is (a claw coming back from its hold can be further off than
     * the reach, its player pulled away by another arm meanwhile)
     */
    wire: 96
});

/** what an arm is doing (two bits of its data) */
export const enum OctoArmState {
    /** on the harness, curled, alive */
    Ready = 0,
    /** shooting out */
    Flying = 1,
    /** clamped onto something, its player pulled toward it */
    Pulling = 2,
    /** coming back, slowly */
    Returning = 3
}

/** an arm as the page sees it: its state, and its claw's place from its player (an angle, a distance: both move) */
export interface OctoArmData {
    readonly state: OctoArmState
    /** where its claw is, as a direction from the player (radians, the world's) */
    readonly angle: number
    /** ...and how far out (units; on the harness: 0) */
    readonly distance: number
}

/** the four arms (`OctoRig.arms` of them), in their order on the harness (left high, right high, left low, right low) */
export type OctoData = OctoArmData[];

/** an arm sitting ready on its harness, as its data says it */
export const OCTO_ARM_READY: OctoArmData = Object.freeze({ state: OctoArmState.Ready, angle: 0, distance: 0 });

/** the page's own name for the gadget's key (console/defaultBinds.ts: Shift by default) */
export const OCTO_ACTION = "octo";
